<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const store = useStore();
const { t } = useI18n();

// Reactive state
const csvFile = ref(null);
const createConversations = ref(false); // NEW: Toggle to create conversations
const selectedLabelIds = ref([]); // NEW: Selected label IDs
const privateMessageTemplate = ref('');
const selectedInboxId = ref(null);
const selectedFunnelId = ref(null);
const selectedStageName = ref('');
const productData = ref({
  enabled: false,
  name: '',
  description: '',
  amount: '',
  currency: 'BRL',
});
const appointmentData = ref({
  enabled: false,
  title: '',
  description: '',
  scheduled_at: '',
  duration_minutes: 60,
  event_type: 'event',
  status: 'scheduled',
  location: '',
});
const notesContent = ref('');
const isProcessing = ref(false);

// Computed
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const funnels = computed(() => store.getters['funnels/getFunnels'] || []);
const labels = computed(() => store.getters['labels/getLabels'] || []);
const selectedFunnel = computed(() => {
  if (!selectedFunnelId.value) return null;
  return store.getters['funnels/getFunnel'](selectedFunnelId.value);
});
const availableStages = computed(() => {
  if (!selectedFunnel.value?.stages) return [];
  return Object.entries(selectedFunnel.value.stages).map(([key, stage]) => ({
    key,
    ...stage,
  }));
});
const uiFlags = computed(() => store.getters['contactImports/getUIFlags']);
const imports = computed(
  () => store.getters['contactImports/getContactImports']
);

// Methods
const handleFileUpload = event => {
  const file = event.target.files[0];

  // FIX #14: Validate CSV file more thoroughly
  if (!file) return;

  const isCSV =
    file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');

  if (!isCSV) {
    useAlert(t('CONTACT_IMPORT.ERRORS.INVALID_FILE_TYPE'));
    event.target.value = '';
    return;
  }

  // Validate file size (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    useAlert(t('CONTACT_IMPORT.ERRORS.FILE_TOO_LARGE'));
    event.target.value = '';
    return;
  }

  csvFile.value = file;
};

const downloadTemplate = async () => {
  try {
    await store.dispatch('contactImports/downloadTemplate');
    useAlert(t('CONTACT_IMPORT.TEMPLATE_DOWNLOADED'));
  } catch (error) {
    useAlert(t('CONTACT_IMPORT.ERRORS.DOWNLOAD_FAILED'));
  }
};

const readFileAsText = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
};

const validateForm = () => {
  if (!csvFile.value) {
    useAlert(t('CONTACT_IMPORT.ERRORS.CSV_REQUIRED'));
    return false;
  }

  // Only validate conversation fields if createConversations is enabled
  if (createConversations.value) {
    if (!privateMessageTemplate.value.trim()) {
      useAlert(t('CONTACT_IMPORT.ERRORS.MESSAGE_REQUIRED'));
      return false;
    }
    if (!selectedInboxId.value) {
      useAlert(t('CONTACT_IMPORT.ERRORS.INBOX_REQUIRED'));
      return false;
    }
    if (!selectedFunnelId.value) {
      useAlert(t('CONTACT_IMPORT.ERRORS.FUNNEL_REQUIRED'));
      return false;
    }
    if (!selectedStageName.value) {
      useAlert(t('CONTACT_IMPORT.ERRORS.STAGE_REQUIRED'));
      return false;
    }
  }
  return true;
};

const resetForm = () => {
  csvFile.value = null;
  createConversations.value = false;
  selectedLabelIds.value = [];
  privateMessageTemplate.value = '';
  selectedInboxId.value = null;
  selectedFunnelId.value = null;
  selectedStageName.value = '';
  productData.value = {
    enabled: false,
    name: '',
    description: '',
    amount: '',
    currency: 'BRL',
  };
  appointmentData.value = {
    enabled: false,
    title: '',
    description: '',
    scheduled_at: '',
    duration_minutes: 60,
    event_type: 'event',
    status: 'scheduled',
    location: '',
  };
  notesContent.value = '';
  const fileInput = document.getElementById('csv-file-input');
  if (fileInput) fileInput.value = '';
};

const toggleLabel = labelId => {
  const index = selectedLabelIds.value.indexOf(labelId);
  if (index === -1) {
    selectedLabelIds.value.push(labelId);
  } else {
    selectedLabelIds.value.splice(index, 1);
  }
};

const submitImport = async () => {
  if (!validateForm()) return;

  isProcessing.value = true;

  try {
    // Read CSV file content
    const csvContent = await readFileAsText(csvFile.value);

    // Prepare import data
    const importPayload = {
      contact_import: {
        create_conversations: createConversations.value,
        label_ids: selectedLabelIds.value,
        inbox_id: createConversations.value ? selectedInboxId.value : null,
        funnel_id: createConversations.value ? selectedFunnelId.value : null,
        stage_name: createConversations.value ? selectedStageName.value : null,
        private_message_template: createConversations.value
          ? privateMessageTemplate.value
          : null,
        notes_content:
          createConversations.value && notesContent.value
            ? notesContent.value
            : null,
        import_data: {
          csv_data: csvContent,
        },
        product_data:
          createConversations.value && productData.value.enabled
            ? {
                name: productData.value.name,
                description: productData.value.description,
                amount: parseFloat(productData.value.amount) || 0,
                currency: productData.value.currency || 'BRL',
              }
            : null,
        appointment_data:
          createConversations.value && appointmentData.value.enabled
            ? {
                title: appointmentData.value.title,
                description: appointmentData.value.description,
                scheduled_at: appointmentData.value.scheduled_at,
                duration_minutes:
                  parseInt(appointmentData.value.duration_minutes, 10) || 60,
                event_type: appointmentData.value.event_type || 'event',
                status: appointmentData.value.status || 'scheduled',
                location: appointmentData.value.location || null,
              }
            : null,
      },
    };

    await store.dispatch('contactImports/create', importPayload);

    useAlert(t('CONTACT_IMPORT.SUCCESS'));

    // Reset form
    resetForm();
  } catch (error) {
    // FIX #13: Better error handling for different response formats
    let errorMessage = t('CONTACT_IMPORT.ERRORS.IMPORT_FAILED');

    if (error.response?.data) {
      const data = error.response.data;

      // Handle array of errors
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        errorMessage = data.errors[0];
      }
      // Handle object of errors (e.g., {stage_name: ['invalid']})
      else if (typeof data.errors === 'object' && data.errors !== null) {
        const firstKey = Object.keys(data.errors)[0];
        if (firstKey && Array.isArray(data.errors[firstKey])) {
          errorMessage = `${firstKey}: ${data.errors[firstKey][0]}`;
        } else if (firstKey) {
          errorMessage = `${firstKey}: ${data.errors[firstKey]}`;
        }
      }
      // Handle simple error message
      else if (data.error) {
        errorMessage = data.error;
      }
      // Handle message field
      else if (data.message) {
        errorMessage = data.message;
      }
    }

    useAlert(errorMessage);
  } finally {
    isProcessing.value = false;
  }
};

const getStatusLabel = status => {
  const statusKey = status.toUpperCase();
  return t(`CONTACT_IMPORT.STATUS.${statusKey}`);
};

const loadData = async () => {
  try {
    await Promise.all([
      store.dispatch('inboxes/get'),
      store.dispatch('funnels/get'),
      store.dispatch('contactImports/get'),
      store.dispatch('labels/get'),
    ]);
  } catch (error) {
    // Error loading data - silently fail as the UI will show empty state
  }
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <section
      class="flex w-full h-full gap-4 overflow-hidden justify-evenly bg-n-background"
    >
      <div class="flex flex-col w-full h-full transition-all duration-300">
        <!-- Header -->
        <div class="p-6 border-b border-n-slate-6">
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ t('CONTACT_IMPORT.TITLE') }}
          </h1>
          <p class="mt-2 text-sm text-n-slate-11">
            {{ t('CONTACT_IMPORT.DESCRIPTION') }}
          </p>
        </div>

        <!-- Main Content -->
        <main class="flex-1 overflow-y-auto">
          <div class="w-full mx-auto max-w-[60rem] p-6 space-y-6">
            <!-- CSV Template Download -->
            <div class="p-4 bg-n-blue-2 rounded-lg border border-n-blue-6">
              <h3 class="text-sm font-medium text-n-blue-11 mb-2">
                {{ t('CONTACT_IMPORT.TEMPLATE.TITLE') }}
              </h3>
              <p class="text-sm text-n-blue-11 mb-3">
                {{ t('CONTACT_IMPORT.TEMPLATE.DESCRIPTION') }}
              </p>
              <button
                type="button"
                class="px-4 py-2 text-sm font-medium text-white bg-n-blue-9 hover:bg-n-blue-10 rounded-md transition-colors"
                @click="downloadTemplate"
              >
                {{ t('CONTACT_IMPORT.TEMPLATE.DOWNLOAD_BUTTON') }}
              </button>
            </div>

            <!-- CSV File Upload -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-n-slate-12">
                {{ t('CONTACT_IMPORT.CSV_FILE') }}
                <span class="text-n-red-9">*</span>
              </label>
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                class="block w-full text-sm text-n-slate-11 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-n-blue-2 file:text-n-blue-11 hover:file:bg-n-blue-3"
                @change="handleFileUpload"
              />
              <p v-if="csvFile" class="text-sm text-n-green-9">
                {{
                  t('CONTACT_IMPORT.FILE_SELECTED', { filename: csvFile.name })
                }}
              </p>
            </div>

            <!-- Labels Selection -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-n-slate-12">
                {{ t('CONTACT_IMPORT.LABELS') }}
              </label>
              <p class="text-sm text-n-slate-11 mb-2">
                {{ t('CONTACT_IMPORT.LABELS_DESCRIPTION') }}
              </p>
              <div
                v-if="labels.length > 0"
                class="flex flex-wrap gap-2 p-3 border border-n-slate-6 rounded-md bg-n-background"
              >
                <button
                  v-for="label in labels"
                  :key="label.id"
                  type="button"
                  class="px-3 py-1 text-sm rounded-full border transition-colors"
                  :class="
                    selectedLabelIds.includes(label.id)
                      ? 'bg-n-blue-9 text-white border-n-blue-9'
                      : 'bg-n-background text-n-slate-12 border-n-slate-6 hover:border-n-blue-9'
                  "
                  :style="
                    selectedLabelIds.includes(label.id)
                      ? { backgroundColor: label.color }
                      : {}
                  "
                  @click="toggleLabel(label.id)"
                >
                  {{ label.title }}
                </button>
              </div>
              <p v-else class="text-sm text-n-slate-9 italic">
                {{ t('CONTACT_IMPORT.NO_LABELS') }}
              </p>
            </div>

            <!-- Create Conversations Toggle -->
            <div
              class="p-4 bg-n-amber-2 rounded-lg border border-n-amber-6 space-y-3"
            >
              <div class="flex items-center">
                <input
                  id="create-conversations"
                  v-model="createConversations"
                  type="checkbox"
                  class="w-4 h-4 text-n-blue-9 border-n-slate-6 rounded focus:ring-n-blue-9"
                />
                <label
                  for="create-conversations"
                  class="ml-2 text-sm font-medium text-n-amber-11"
                >
                  {{ t('CONTACT_IMPORT.CREATE_CONVERSATIONS') }}
                </label>
              </div>
              <p class="text-sm text-n-amber-11">
                {{ t('CONTACT_IMPORT.CREATE_CONVERSATIONS_DESCRIPTION') }}
              </p>
            </div>

            <!-- Conversation Settings (only shown when createConversations is enabled) -->
            <div v-if="createConversations" class="space-y-6">
              <!-- Private Message Template -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-n-slate-12">
                  {{ t('CONTACT_IMPORT.PRIVATE_MESSAGE') }}
                  <span class="text-n-red-9">*</span>
                </label>
                <textarea
                  v-model="privateMessageTemplate"
                  rows="4"
                  class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9 bg-n-background text-n-slate-12"
                  :placeholder="t('CONTACT_IMPORT.MESSAGE_PLACEHOLDER')"
                />
              </div>

              <!-- Inbox Selection -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-n-slate-12">
                  {{ t('CONTACT_IMPORT.INBOX') }}
                  <span class="text-n-red-9">*</span>
                </label>
                <select
                  v-model="selectedInboxId"
                  class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9 bg-n-background text-n-slate-12"
                >
                  <option :value="null">
                    {{ t('CONTACT_IMPORT.SELECT_INBOX') }}
                  </option>
                  <option
                    v-for="inbox in inboxes"
                    :key="inbox.id"
                    :value="inbox.id"
                  >
                    {{ inbox.name }}
                  </option>
                </select>
              </div>

              <!-- Funnel Selection -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-n-slate-12">
                  {{ t('CONTACT_IMPORT.FUNNEL') }}
                  <span class="text-n-red-9">*</span>
                </label>
                <select
                  v-model="selectedFunnelId"
                  class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9 bg-n-background text-n-slate-12"
                  @change="selectedStageName = ''"
                >
                  <option :value="null">
                    {{ t('CONTACT_IMPORT.SELECT_FUNNEL') }}
                  </option>
                  <option
                    v-for="funnel in funnels"
                    :key="funnel.id"
                    :value="funnel.id"
                  >
                    {{ funnel.name }}
                  </option>
                </select>
              </div>

              <!-- Stage Selection -->
              <div v-if="selectedFunnelId" class="space-y-2">
                <label class="block text-sm font-medium text-n-slate-12">
                  {{ t('CONTACT_IMPORT.STAGE') }}
                  <span class="text-n-red-9">*</span>
                </label>
                <select
                  v-model="selectedStageName"
                  class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9 bg-n-background text-n-slate-12"
                >
                  <option value="">
                    {{ t('CONTACT_IMPORT.SELECT_STAGE') }}
                  </option>
                  <option
                    v-for="stage in availableStages"
                    :key="stage.key"
                    :value="stage.key"
                  >
                    {{ stage.label }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Optional fields only shown when createConversations is enabled -->
            <template v-if="createConversations">
              <!-- Optional: Product -->
              <div class="border border-n-slate-6 rounded-lg p-4 space-y-3">
                <div class="flex items-center">
                  <input
                    id="product-enabled"
                    v-model="productData.enabled"
                    type="checkbox"
                    class="w-4 h-4 text-n-blue-9 border-n-slate-6 rounded focus:ring-n-blue-9"
                  />
                  <label
                    for="product-enabled"
                    class="ml-2 text-sm font-medium text-n-slate-12"
                  >
                    {{ t('CONTACT_IMPORT.PRODUCT.TITLE') }}
                  </label>
                </div>

                <div v-if="productData.enabled" class="space-y-3 ml-6">
                  <input
                    v-model="productData.name"
                    type="text"
                    class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                    :placeholder="t('CONTACT_IMPORT.PRODUCT.NAME')"
                  />
                  <input
                    v-model="productData.description"
                    type="text"
                    class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                    :placeholder="t('CONTACT_IMPORT.PRODUCT.DESCRIPTION')"
                  />
                  <div class="grid grid-cols-2 gap-3">
                    <input
                      v-model="productData.amount"
                      type="number"
                      step="0.01"
                      class="px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                      :placeholder="
                        t('CONTACT_IMPORT.PRODUCT.VALUE_PLACEHOLDER')
                      "
                    />
                    <select
                      v-model="productData.currency"
                      class="px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                    >
                      <option value="BRL">
                        {{ t('CONTACT_IMPORT.PRODUCT.CURRENCY_BRL') }}
                      </option>
                      <option value="USD">
                        {{ t('CONTACT_IMPORT.PRODUCT.CURRENCY_USD') }}
                      </option>
                      <option value="EUR">
                        {{ t('CONTACT_IMPORT.PRODUCT.CURRENCY_EUR') }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Optional: Appointment -->
              <div class="border border-n-slate-6 rounded-lg p-4 space-y-3">
                <div class="flex items-center">
                  <input
                    id="appointment-enabled"
                    v-model="appointmentData.enabled"
                    type="checkbox"
                    class="w-4 h-4 text-n-blue-9 border-n-slate-6 rounded focus:ring-n-blue-9"
                  />
                  <label
                    for="appointment-enabled"
                    class="ml-2 text-sm font-medium text-n-slate-12"
                  >
                    {{ t('CONTACT_IMPORT.APPOINTMENT.TITLE') }}
                  </label>
                </div>

                <div v-if="appointmentData.enabled" class="space-y-3 ml-6">
                  <input
                    v-model="appointmentData.title"
                    type="text"
                    class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                    :placeholder="
                      t('CONTACT_IMPORT.APPOINTMENT.TITLE_PLACEHOLDER')
                    "
                  />
                  <textarea
                    v-model="appointmentData.description"
                    rows="2"
                    class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                    :placeholder="
                      t('CONTACT_IMPORT.APPOINTMENT.DESCRIPTION_PLACEHOLDER')
                    "
                  />
                  <div class="grid grid-cols-2 gap-3">
                    <input
                      v-model="appointmentData.scheduled_at"
                      type="datetime-local"
                      class="px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                    />
                    <input
                      v-model="appointmentData.duration_minutes"
                      type="number"
                      min="15"
                      step="15"
                      class="px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                      :placeholder="
                        t('CONTACT_IMPORT.APPOINTMENT.DURATION_PLACEHOLDER')
                      "
                    />
                  </div>
                  <div class="grid grid-cols-2 gap-3">
                    <select
                      v-model="appointmentData.event_type"
                      class="px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                    >
                      <option value="event">
                        {{ t('CONTACT_IMPORT.APPOINTMENT.TYPE_EVENT') }}
                      </option>
                      <option value="task">
                        {{ t('CONTACT_IMPORT.APPOINTMENT.TYPE_TASK') }}
                      </option>
                    </select>
                    <select
                      v-model="appointmentData.status"
                      class="px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                    >
                      <option value="scheduled">
                        {{ t('CONTACT_IMPORT.APPOINTMENT.STATUS_SCHEDULED') }}
                      </option>
                      <option value="confirmed">
                        {{ t('CONTACT_IMPORT.APPOINTMENT.STATUS_CONFIRMED') }}
                      </option>
                      <option value="completed">
                        {{ t('CONTACT_IMPORT.APPOINTMENT.STATUS_COMPLETED') }}
                      </option>
                      <option value="cancelled">
                        {{ t('CONTACT_IMPORT.APPOINTMENT.STATUS_CANCELLED') }}
                      </option>
                      <option value="no_show">
                        {{ t('CONTACT_IMPORT.APPOINTMENT.STATUS_NO_SHOW') }}
                      </option>
                    </select>
                  </div>
                  <input
                    v-model="appointmentData.location"
                    type="text"
                    class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                    :placeholder="
                      t('CONTACT_IMPORT.APPOINTMENT.LOCATION_PLACEHOLDER')
                    "
                  />
                </div>
              </div>

              <!-- Optional: Notes -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-n-slate-12">
                  {{ t('CONTACT_IMPORT.NOTES') }}
                </label>
                <textarea
                  v-model="notesContent"
                  rows="3"
                  class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9 bg-n-background text-n-slate-12"
                  :placeholder="t('CONTACT_IMPORT.NOTES_PLACEHOLDER')"
                />
              </div>
            </template>

            <!-- Submit Button -->
            <div class="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                class="px-6 py-2 text-sm font-medium text-n-slate-12 bg-n-background border border-n-slate-6 rounded-md hover:bg-n-slate-2 focus:outline-none focus:ring-2 focus:ring-n-blue-9"
                @click="resetForm"
              >
                {{ t('CONTACT_IMPORT.RESET') }}
              </button>
              <button
                type="button"
                class="px-6 py-2 text-sm font-medium text-white bg-n-blue-9 rounded-md hover:bg-n-blue-10 focus:outline-none focus:ring-2 focus:ring-n-blue-9 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isProcessing || uiFlags.isCreating"
                @click="submitImport"
              >
                <span v-if="isProcessing || uiFlags.isCreating">
                  {{ t('CONTACT_IMPORT.PROCESSING') }}
                </span>
                <span v-else>
                  {{ t('CONTACT_IMPORT.SUBMIT') }}
                </span>
              </button>
            </div>

            <!-- Recent Imports -->
            <div
              v-if="imports.length > 0"
              class="mt-8 border-t border-n-slate-6 pt-6"
            >
              <h2 class="text-lg font-semibold text-n-slate-12 mb-4">
                {{ t('CONTACT_IMPORT.RECENT_IMPORTS') }}
              </h2>
              <div class="space-y-3">
                <div
                  v-for="importRecord in imports.slice(0, 5)"
                  :key="importRecord.id"
                  class="p-4 bg-n-slate-2 rounded-lg border border-n-slate-6"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex-1">
                      <div class="flex items-center space-x-3">
                        <span
                          class="px-2 py-1 text-xs font-medium rounded-full"
                          :class="{
                            'bg-n-yellow-3 text-n-yellow-11':
                              importRecord.status === 'pending',
                            'bg-n-blue-3 text-n-blue-11':
                              importRecord.status === 'processing',
                            'bg-n-green-3 text-n-green-11':
                              importRecord.status === 'completed',
                            'bg-n-red-3 text-n-red-11':
                              importRecord.status === 'failed',
                          }"
                        >
                          {{ getStatusLabel(importRecord.status) }}
                        </span>
                        <span
                          v-if="importRecord.create_conversations"
                          class="px-2 py-1 text-xs font-medium rounded-full bg-n-blue-3 text-n-blue-11"
                        >
                          {{ t('CONTACT_IMPORT.WITH_CONVERSATIONS') }}
                        </span>
                        <span
                          v-else
                          class="px-2 py-1 text-xs font-medium rounded-full bg-n-slate-3 text-n-slate-11"
                        >
                          {{ t('CONTACT_IMPORT.CONTACTS_ONLY') }}
                        </span>
                        <span
                          v-if="importRecord.funnel"
                          class="text-sm text-n-slate-11"
                        >
                          {{ importRecord.funnel.name }}
                          <span class="text-n-slate-9">/</span>
                          {{ importRecord.stage_name }}
                        </span>
                      </div>
                      <div class="mt-2 text-sm text-n-slate-12">
                        {{
                          t('CONTACT_IMPORT.PROGRESS', {
                            processed: importRecord.processed_contacts,
                            total: importRecord.total_contacts,
                            successful: importRecord.successful_imports,
                            failed: importRecord.failed_imports,
                          })
                        }}
                      </div>
                    </div>
                    <div class="text-right">
                      <div class="text-sm font-medium text-n-slate-12">
                        {{ importRecord.progress_percentage }}<span>%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </section>
  </div>
</template>
